import { CircleConsentTypes } from '@calm/evexia-web-sdk';
import {
    errorBodyQueryString,
    errorTitleQueryString,
    ftueQueryString,
    packClassQueryString,
    programIdQueryString,
    showOverviewQueryString,
    redirectQueryString,
    friendsAndFamilyIdString,
    notificationsTypeString,
} from './constants';
import { SupportedLanguageType } from './intl';

export function addLangParam(lang: string, url?: string) {
    if (!url) {
        return '#';
    }

    const isFaqLink = url.includes('/faq');
    const nonEnglishOrSpanish =
        lang !== SupportedLanguageType.EN && lang !== SupportedLanguageType.ES;

    if (
        lang === SupportedLanguageType.EN ||
        (isFaqLink && nonEnglishOrSpanish)
    ) {
        return url;
    }

    return `${url}?lang=${lang}`;
}

function withLangParam(url: string) {
    return (lang: string) => addLangParam(lang, url);
}

export const routes = {
    auth: '/auth',
    sso: '/auth/sso',
    ssoInfo: '/auth/sso/info',
    saml: '/saml',
    /** TODO rename to landing */
    dashboard: '/',
    reconsent: '/reconsent',
    library: '/library',
    discover: '/discover',
    discoverClinical: '/discover/clinical',
    discoverPack: (packClass = '', viewAll = false) =>
        `/discover/pack/${packClass}${viewAll ? '?viewAll=true' : ''}`,
    discoverDailies: (dailyId = '') =>
        dailyId ? `/discover/dailies/${dailyId}` : '/discover/dailies',
    favorites: '/favorites',
    health: '/health',
    login: '/login',
    careplan: '/care-plan',
    completed: '/completed',
    carePlanAllRecommendations: '/care-plan/all-recommendations',
    expired: '/expired',
    profile: '/profile',
    profileInvite: (params: { queryId?: string }) =>
        `/profile/invite${
            params.queryId
                ? `?${new URLSearchParams({
                      [friendsAndFamilyIdString]: params.queryId,
                  })}`
                : ''
        }`,
    profileInviteStatus: (params: { queryId?: string }) =>
        `/profile/invite/status${
            params.queryId
                ? `?${new URLSearchParams({
                      [friendsAndFamilyIdString]: params.queryId,
                  })}`
                : ''
        }`,
    programDetails: (packClass = '', programId = '', showOverview = '') =>
        `/program${
            programId && packClass
                ? `?${new URLSearchParams({
                      ...(packClass
                          ? { [packClassQueryString]: packClass }
                          : {}),
                      ...(programId
                          ? { [programIdQueryString]: programId }
                          : {}),
                      ...(showOverview
                          ? { [showOverviewQueryString]: showOverview }
                          : {}),
                  }).toString()}`
                : ''
        }`,
    recent: '/recent',
    resources: '/resources',
    screening: ({
        packClass = '',
        programId = '',
        isFtue = false,
        skipToQuestion = false,
        isRetrigger = false,
        redirect,
    }: {
        packClass?: string;
        programId?: string;
        isFtue?: boolean;
        redirect?: string;
        skipToQuestion?: boolean;
        isRetrigger?: boolean;
    }) =>
        `/screening${
            !!packClass ||
            !!programId ||
            isFtue ||
            redirect ||
            skipToQuestion ||
            isRetrigger
                ? `?${new URLSearchParams({
                      ...(packClass
                          ? { [packClassQueryString]: packClass }
                          : {}),
                      ...(programId
                          ? { [programIdQueryString]: programId }
                          : {}),
                      ...(redirect ? { [redirectQueryString]: redirect } : {}),
                      ...(isFtue ? { [ftueQueryString]: 'true' } : {}),
                      ...(skipToQuestion ? { skipToQuestion: 'true' } : {}),
                      ...(isRetrigger ? { isRetrigger: 'true' } : {}),
                  })}`
                : ''
        }`,
    signup: '/signup',
    topics: (params: { isFtue: boolean }) =>
        `/topics${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    goals: (params: { isFtue: boolean }) =>
        `/goals${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    conditions: (params: { isFtue: boolean }) =>
        `/conditions${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    results: (params: { isFtue: boolean }) =>
        `/results${
            params.isFtue
                ? `?${new URLSearchParams({ [ftueQueryString]: 'true' })}`
                : ''
        }`,
    registration: '/registration',
    emailOptInConfirm: '/registration/email-communications-confirm',
    ftue: '/ftue',
    notFound: '/404',
    logout: '/logout',
    error: (params: { title?: string; body?: string }) =>
        `/500${
            params.title || params.body
                ? `?${new URLSearchParams({
                      ...(params.title
                          ? { [errorTitleQueryString]: params.title }
                          : {}),
                      ...(params.body
                          ? { [errorBodyQueryString]: params.body }
                          : {}),
                  }).toString()}`
                : ''
        }`,
    settings: '/profile/settings',
    notifications: (value = '') =>
        `/profile/notifications${
            value
                ? `?${new URLSearchParams({
                      ...(value ? { [notificationsTypeString]: value } : {}),
                  }).toString()}`
                : ''
        }`,
    sponsor: '/profile/sponsor',
    standaloneScreening: '/mental-health-screening',
    standaloneResults: '/mental-health-screening-results',
    nonUsResident: '/non-us-resident',
    external: {
        consentFallback: {
            chdpp: withLangParam(
                'https://www.calmhealth.com/consent-to-collect-consumer-health-data'
            ),
        } as Record<CircleConsentTypes, (lang: string) => string>,
        tos: withLangParam('https://www.calmhealth.com/terms-of-service'),
        faq: withLangParam('https://www.calmhealth.com/faq'),
        manageNotifications: withLangParam(
            'https://support.calm.com/hc/en-us/articles/31817784274715-Managing-Your-Calm-Health-Notifications'
        ),
        cookieNotice: withLangParam('https://www.calmhealth.com/cookies'),
        privacyPolicy: withLangParam(
            'https://www.calmhealth.com/privacy-policy'
        ),
        consumerHealthDataPrivacyPolicy: withLangParam(
            'https://www.calmhealth.com/consumer-health-data-privacy-policy'
        ),
        calmScreeningLandingPage: withLangParam('https://screening.calm.com'),
        /**
         * This app store deeplink is in a new AppsFlyer template set, and thus
         * doesn't share the id used elsewhere. This means we can't just update the
         * NEXT_PUBLIC_LINK_DEEPLINK_BASE_URL env var that includes the template id
         * for any deeplinks around the app that leverage /careplan, /profile, etc.
         * since those deeplinks are not set up within this template group.
         *
         * AppsFlyer link structure: https://link.calmhealth.com/[template Id]/[link Id]
         */
        appDownloadDeeplink: 'https://link.calmhealth.com/S4oG/tf7n67c6',
    },
};
